import {
  civilDateAsUtcDate,
  civilDateInZone,
  formatCivilDateLabel,
  parseCivilDate,
  zoneOffsetMs,
} from "./civilDate";
import type { CivilDate } from "./civilDate";
import type { Depot } from "./types";

/**
 * The window the fleet views report energy over — `?from&to` on `/charge-points`
 * and `/depots` (server spec §8.3).
 *
 * ## Why the URL carries a range and not two timestamps
 *
 * The server takes instants, and this module turns "August" into the two it
 * means. The alternative — putting the resolved `from`/`to` in the URL — was
 * rejected because a link would then freeze the month it was copied in: an
 * operator pasting "this month" into a ticket in August would have a colleague
 * open a fixed August window in October and not notice. A range is the thing the
 * operator actually chose, and it keeps meaning that.
 *
 * ## Why a zone has to be named
 *
 * A calendar month is not an interval until somebody says whose. Spec 6.1 draws
 * every period boundary in the depot's timezone, and `2026-08-01T00:00:00Z` is
 * 07:00 on the first in Phnom Penh — so a naive UTC month quietly reports seven
 * hours of the month's charging under July. Every window here is resolved in a
 * stated zone, and the zone is shown beside the figure it produced.
 *
 * All of it is computed with `Intl` and no date library. The three operations
 * needed — a zone's offset at an instant, local midnight as an instant, and month
 * arithmetic — are each a few lines, and the tests pin the boundaries that matter.
 */

/** A window whose boundaries this module chooses from the clock. */
export type PresetRange = "month" | "previous-month" | "last-7" | "last-30";

/** A preset, or a pair of dates the operator typed. */
export type UsageRange = PresetRange | "custom";

export interface ResolvedWindow {
  /** Inclusive. */
  readonly from: Date;
  /** Exclusive, matching the server's half-open `[from, to)`. */
  readonly to: Date;
  /** What to call it on screen: "August 2026", "Last 7 days". */
  readonly label: string;
  /** The zone the boundaries were drawn in, shown so a figure is never unqualified. */
  readonly zone: string;
}

const PRESETS: readonly PresetRange[] = ["month", "previous-month", "last-7", "last-30"];
const RANGES: readonly UsageRange[] = [...PRESETS, "custom"];

/** The picker's options, in the order they appear. */
export const USAGE_RANGES: readonly { readonly value: UsageRange; readonly label: string }[] = [
  { value: "month", label: "This month" },
  { value: "previous-month", label: "Last month" },
  { value: "last-7", label: "Last 7 days" },
  { value: "last-30", label: "Last 30 days" },
  { value: "custom", label: "Custom range" },
];

/**
 * A URL parameter is whatever the last person typed. An unrecognised range means
 * no energy column at all, rather than a window this code invented — a stale
 * `?range=quarter` should look like the range was never set, because it wasn't.
 */
export function isUsageRange(raw: string | null): raw is UsageRange {
  return raw !== null && (RANGES as readonly string[]).includes(raw);
}

/**
 * Which calendar the boundaries are drawn in.
 *
 * One selected site answers it outright. With no selection, a deployment whose
 * sites all share a zone still has one honest answer — which is every deployment
 * this server currently has. Only a genuinely multi-zone fleet with no site
 * chosen has none, and there the answer is UTC and it says UTC: picking one
 * site's calendar for a figure covering several would label a total with a month
 * it does not cover.
 */
export function reportingZone(depots: readonly Depot[], depotId: string): string {
  const selected = depots.find((depot) => depot.id === depotId);
  if (selected !== undefined) return selected.timezone;

  const zones = new Set(depots.map((depot) => depot.timezone));
  return zones.size === 1 ? (depots[0]?.timezone ?? "UTC") : "UTC";
}

const MONTH_LABEL = new Intl.DateTimeFormat("en-GB", { month: "long", year: "numeric" });

export function resolveWindow(
  range: PresetRange,
  zone: string,
  now: Date = new Date(),
): ResolvedWindow {
  const today = civilDateInZone(zone, now);

  if (range === "month" || range === "previous-month") {
    const monthOffset = range === "month" ? 0 : -1;
    const start = addMonths(today.year, today.month, monthOffset);
    const end = addMonths(start.year, start.month, 1);

    return {
      from: instantAtLocalMidnight(zone, start.year, start.month, 1),
      to: instantAtLocalMidnight(zone, end.year, end.month, 1),
      // Formatted from the resolved month rather than from `now`: on the first of
      // the month the two disagree by a day either side of UTC, and the label has
      // to name the month whose boundaries were actually used.
      label: MONTH_LABEL.format(new Date(Date.UTC(start.year, start.month - 1, 15))),
      zone,
    };
  }

  /**
   * Whole local days, today included, rather than a rolling 24n hours. A total
   * that starts at 11:00 last Saturday cannot be reconciled against any daily
   * figure, and over a DST change a fixed duration lands on 01:00 — a boundary
   * that exists nowhere in the operator's calendar.
   */
  const days = range === "last-7" ? 7 : 30;
  return { ...resolveLastDays(days, zone, now), label: `Last ${days} days` };
}

/**
 * The last `days` whole local days, today included: from local midnight `days - 1`
 * days ago to the midnight that ends today. Unlabelled, because what to call one
 * day ("Today") or seven is the caller's vocabulary.
 */
export function resolveLastDays(
  days: number,
  zone: string,
  now: Date = new Date(),
): Omit<ResolvedWindow, "label"> {
  const today = civilDateInZone(zone, now);
  const to = instantAtLocalMidnight(zone, today.year, today.month, today.day + 1);
  const from = instantAtLocalMidnight(zone, today.year, today.month, today.day - (days - 1));

  return { from, to, zone };
}

/**
 * A custom window as it sits in the URL: two calendar dates, each with an
 * optional time. Raw, because these come straight off search parameters and any
 * of them may be absent, stale, or hand-edited.
 */
export interface CustomWindowInput {
  readonly fromDate: string | null;
  readonly fromTime: string | null;
  readonly toDate: string | null;
  readonly toTime: string | null;
}

/**
 * Three states, not two, and the third is the point.
 *
 * Choosing "Custom range" from the picker sets the range before either date
 * exists, so there is a moment — and, from a half-written link, a lasting state —
 * where a window is *intended* but not yet described. Collapsing that into "off"
 * would show a table with no energy column and no reason for it, which reads as a
 * bug. `pending` carries the sentence that says what is missing.
 *
 * It must also never be sent: `?from` without `?to` is a 400 from
 * `parseUsageWindow`, and that 400 is the fleet list's response, so a
 * half-specified window would take the whole table down rather than one column.
 */
export type UsageWindowState =
  | { readonly kind: "off" }
  | { readonly kind: "pending"; readonly message: string }
  | { readonly kind: "resolved"; readonly window: ResolvedWindow };

const OFF: UsageWindowState = { kind: "off" };

/**
 * The window the fleet views should report, read from the URL.
 *
 * The single entry point both pages use: they hold five search parameters and one
 * zone, and this turns them into a column or into the reason there isn't one.
 */
export function readUsageWindow(
  range: string | null,
  custom: CustomWindowInput,
  zone: string,
  now: Date = new Date(),
): UsageWindowState {
  if (range === "custom") return resolveCustom(custom, zone);
  // Not a range this build offers — a stale `?range=quarter` — is indistinguishable
  // from no range, because it is not a window and inventing one would be worse.
  if (!isPresetRange(range)) return OFF;

  return { kind: "resolved", window: resolveWindow(range, zone, now) };
}

function isPresetRange(raw: string | null): raw is PresetRange {
  return raw !== null && (PRESETS as readonly string[]).includes(raw);
}

const TIME_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)(?::[0-5]\d)?$/;

const BAD_DATE = "Dates must be calendar dates, as YYYY-MM-DD.";
const BAD_TIME = "Times must be a 24-hour HH:MM.";

function resolveCustom(input: CustomWindowInput, zone: string): UsageWindowState {
  const fromDate = parseCivilDate(input.fromDate);
  const toDate = parseCivilDate(input.toDate);

  // Which end is missing, rather than "fill in the form": the operator is one
  // field away and the sentence should say which one.
  if (fromDate === "absent" && toDate === "absent") {
    return { kind: "pending", message: "Choose a start and an end date." };
  }
  if (fromDate === "absent") return { kind: "pending", message: "Choose a start date." };
  if (toDate === "absent") return { kind: "pending", message: "Choose an end date." };
  if (fromDate === "invalid" || toDate === "invalid") {
    return { kind: "pending", message: BAD_DATE };
  }

  const fromTime = parseTime(input.fromTime);
  const toTime = parseTime(input.toTime);
  if (fromTime === "invalid" || toTime === "invalid") {
    return { kind: "pending", message: BAD_TIME };
  }

  const from = instantAtLocalTime(zone, fromDate, fromTime === "absent" ? START_OF_DAY : fromTime);

  /**
   * The rule the whole feature turns on. A stated end time is a boundary and is
   * used as typed; a blank one means the operator named a *day*, and the day they
   * named has to be inside the window. The server's interval is half-open, so
   * including the closing day means closing at the midnight that ends it — which
   * is the next date's midnight, resolved in the same calendar.
   *
   * Getting this wrong is invisible: every month-end figure would simply be one
   * day short, and the year would still add up, because the missing day lands in
   * the next window an operator asks for.
   */
  const to =
    toTime === "absent"
      ? instantAtLocalTime(zone, { ...toDate, day: toDate.day + 1 }, START_OF_DAY)
      : instantAtLocalTime(zone, toDate, toTime);

  if (to.getTime() <= from.getTime()) {
    return { kind: "pending", message: "The end must be after the start." };
  }

  return {
    kind: "resolved",
    window: {
      from,
      to,
      label: describe(fromDate, fromTime, toDate, toTime),
      zone,
    },
  };
}

const START_OF_DAY: TimeOfDay = { hour: 0, minute: 0 };

interface TimeOfDay {
  readonly hour: number;
  readonly minute: number;
}

/**
 * Seconds are accepted and dropped. `<input type="time">` yields `HH:MM` until it
 * is given a finer step, and no operator means a window boundary to the second —
 * but a URL carrying one should narrow to the minute rather than be refused.
 */
function parseTime(raw: string | null): TimeOfDay | "absent" | "invalid" {
  if (raw === null || raw === "") return "absent";

  const match = TIME_PATTERN.exec(raw);
  if (match === null) return "invalid";

  return { hour: Number(match[1]), minute: Number(match[2]) };
}

const MONTH_YEAR_SHORT = new Intl.DateTimeFormat("en-GB", {
  timeZone: "UTC",
  month: "short",
  year: "numeric",
});

function describe(
  fromDate: CivilDate,
  fromTime: TimeOfDay | "absent",
  toDate: CivilDate,
  toTime: TimeOfDay | "absent",
): string {
  const sameDay =
    fromDate.year === toDate.year && fromDate.month === toDate.month && fromDate.day === toDate.day;

  /**
   * The label names what the operator typed, not what the server is sent. With a
   * blank end time those differ by a day — the request closes at the following
   * midnight — and a heading reading "1 Apr" over a figure asked for as March
   * would look like an off-by-one in the total rather than in the caption.
   */
  if (fromTime === "absent" && toTime === "absent") {
    if (sameDay) return formatCivilDateLabel(fromDate);

    // A whole month is the common case and reads badly spelled out twice.
    if (fromDate.year === toDate.year && fromDate.month === toDate.month) {
      const month = MONTH_YEAR_SHORT.format(civilDateAsUtcDate(toDate));
      return `${fromDate.day}–${toDate.day} ${month}`;
    }

    return `${formatCivilDateLabel(fromDate)} – ${formatCivilDateLabel(toDate)}`;
  }

  if (sameDay && fromTime !== "absent" && toTime !== "absent") {
    return `${formatCivilDateLabel(fromDate)} ${clock(fromTime)}–${clock(toTime)}`;
  }

  return `${stamp(fromDate, fromTime)} – ${stamp(toDate, toTime)}`;
}

function stamp(date: CivilDate, time: TimeOfDay | "absent"): string {
  return time === "absent"
    ? formatCivilDateLabel(date)
    : `${formatCivilDateLabel(date)} ${clock(time)}`;
}

function clock(time: TimeOfDay): string {
  return `${String(time.hour).padStart(2, "0")}:${String(time.minute).padStart(2, "0")}`;
}

function addMonths(year: number, month: number, delta: number): { year: number; month: number } {
  // Zero-based arithmetic so December + 1 rolls the year instead of producing a
  // thirteenth month.
  const zeroBased = month - 1 + delta;
  return { year: year + Math.floor(zeroBased / 12), month: (((zeroBased % 12) + 12) % 12) + 1 };
}

/**
 * The instant at which the wall clock in `zone` reads this date and time.
 *
 * `day` may be out of range — `day - 29` is routinely zero or negative, and the
 * inclusive end of a window is routinely the 32nd — and `Date.UTC` normalises it,
 * which is exactly the month arithmetic wanted.
 *
 * Resolved in two passes. The first guesses with the offset in force at the naive
 * UTC instant; the second checks the offset in force at the answer, because a
 * boundary within a day of a DST transition can sit on the other side of it. When
 * they disagree the second is correct: it is the offset actually in effect at the
 * instant being named.
 *
 * A wall-clock reading the zone skips — 02:30 on a spring-forward day — has no
 * instant to return, and the two passes settle on the last reading before the
 * jump rather than throwing. Deterministic and an hour early, which is the
 * failure an operator can see in the label; the alternative, normalising forward
 * a whole day, is the one they cannot.
 */
function instantAtLocalTime(zone: string, date: CivilDate, time: TimeOfDay): Date {
  const naive = Date.UTC(date.year, date.month - 1, date.day, time.hour, time.minute);

  const firstPass = naive - zoneOffsetMs(zone, new Date(naive));
  const secondPass = naive - zoneOffsetMs(zone, new Date(firstPass));

  return new Date(secondPass);
}

function instantAtLocalMidnight(zone: string, year: number, month: number, day: number): Date {
  return instantAtLocalTime(zone, { year, month, day }, START_OF_DAY);
}
